import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import AlarmIcon from "@mui/icons-material/AlarmAddOutlined";
import PersonIcon from "@mui/icons-material/PersonOutline";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { format, isPast } from "date-fns";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastSuccess from "../../errors/toastSuccess";
import { useTranslation } from "react-i18next";

// "Para quem" (Fred 04/11): Cliente = envio agendado de sempre; Só para mim =
// lembrete pessoal (mesmo aviso, som e cartão do lembrete do comentário),
// ligado à mensagem que está em "Responder" ou à última da conversa.
const AMBAR = "#f57c00";

const ScheduledMessageModal = ({ open, onClose, ticketId, initialBody = "", onScheduled, replyingMessageId }) => {
  const { t } = useTranslation();
  const [body, setBody] = useState(initialBody);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [destino, setDestino] = useState("cliente");
  const [lembretes, setLembretes] = useState([]);
  const paraMim = destino === "mim";

  useEffect(() => {
    if (open) {
      setBody(initialBody);
      setScheduledAt(null);
      setDestino("cliente");
      fetchScheduled();
    }
  }, [open, ticketId]);

  const fetchScheduled = async () => {
    try {
      setLoadingList(true);
      const { data } = await api.get(`/scheduled-messages/ticket/${ticketId}`);
      setScheduled(data);
      try {
        const r = await api.get(`/lembretes/ticket/${ticketId}/meus-pendentes`);
        setLembretes(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        setLembretes([]);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSave = async () => {
    if (!body.trim()) {
      toastError(new Error(t("scheduledMessage.errors.emptyBody")));
      return;
    }
    if (!scheduledAt) {
      toastError(new Error(t("scheduledMessage.errors.noDate")));
      return;
    }
    if (isPast(scheduledAt)) {
      toastError(new Error(t("scheduledMessage.errors.pastDate")));
      return;
    }

    if (paraMim) {
      try {
        setLoading(true);
        await api.post("/lembretes", {
          ticketId,
          body: body.trim(),
          remindAt: scheduledAt.toISOString(),
          messageId: replyingMessageId || undefined
        });
        toastSuccess(`Lembrete marcado para ${format(scheduledAt, "dd/MM HH:mm")}. Só você será avisado.`);
        setBody("");
        setScheduledAt(null);
        if (onScheduled) onScheduled();
        await fetchScheduled();
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      await api.post("/scheduled-messages", {
        ticketId,
        body,
        scheduledAt: scheduledAt.toISOString()
      });
      toastSuccess(t("scheduledMessage.success.created"));
      setBody("");
      setScheduledAt(null);
      if (onScheduled) onScheduled();
      await fetchScheduled();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelarLembrete = async (id) => {
    try {
      await api.put(`/message-comments/${id}/lembrete`, { remindAt: null });
      await fetchScheduled();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.delete(`/scheduled-messages/${id}`);
      toastSuccess(t("scheduledMessage.success.cancelled"));
      await fetchScheduled();
    } catch (err) {
      toastError(err);
    }
  };

  const statusColor = (status) => {
    if (status === "pending") return "warning";
    if (status === "sent") return "success";
    if (status === "error") return "error";
    if (status === "cancelled") return "default";
    return "default";
  };

  const statusLabel = (status) => {
    const labels = {
      pending: t("scheduledMessage.status.pending"),
      sent: t("scheduledMessage.status.sent"),
      error: t("scheduledMessage.status.error"),
      cancelled: t("scheduledMessage.status.cancelled")
    };
    return labels[status] || status;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: paraMim ? { border: `2px solid ${AMBAR}` } : undefined }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, ...(paraMim ? { backgroundColor: "rgba(255, 193, 7, 0.15)" } : {}) }}>
          {paraMim ? <AlarmIcon sx={{ color: AMBAR }} /> : <ScheduleIcon color="primary" />}
          {paraMim ? "Lembrete para mim" : t("scheduledMessage.title")}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Para quem</Typography>
              <ToggleButtonGroup exclusive size="small" fullWidth value={destino} onChange={(e, v) => { if (v) setDestino(v); }}>
                <ToggleButton value="cliente" sx={{ textTransform: "none", gap: 0.5 }}><PersonIcon fontSize="small" />Cliente</ToggleButton>
                <ToggleButton value="mim" sx={{ textTransform: "none", gap: 0.5, "&.Mui-selected": { color: AMBAR, backgroundColor: "rgba(255, 193, 7, 0.18)" } }}><AlarmIcon fontSize="small" />Só para mim (lembrete)</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {paraMim && (
              <Alert severity="warning" icon={<AlarmIcon fontSize="inherit" />}>
                Nada vai para o cliente. Na hora marcada, só você é avisado (som e aviso na tela), e a conversa mostra o lembrete
                {replyingMessageId ? " ligado à mensagem que você está respondendo." : " ligado à última mensagem da conversa."}
              </Alert>
            )}
            <TextField
              label={paraMim ? "Do que você quer ser lembrado?" : t("scheduledMessage.fields.message")}
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
              variant="outlined"
              placeholder={paraMim ? "Ex.: ver o backup do Gustavo" : t("scheduledMessage.fields.messagePlaceholder")}
            />

            <DateTimePicker
              label={paraMim ? "Data e hora do lembrete" : t("scheduledMessage.fields.dateTime")}
              value={scheduledAt}
              onChange={(val) => setScheduledAt(val)}
              minDateTime={new Date()}
              ampm={false}
              renderInput={(params) => (
                <TextField {...params} fullWidth variant="outlined" />
              )}
            />

            <Button
              variant="contained"
              color={paraMim ? "warning" : "primary"}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : (paraMim ? <AlarmIcon /> : <EventIcon />)}
              onClick={handleSave}
              disabled={loading || !body.trim() || !scheduledAt}
              fullWidth
            >
              {loading ? t("scheduledMessage.saving") : (paraMim ? "Agendar lembrete (não vai para o cliente)" : t("scheduledMessage.schedule"))}
            </Button>

            {lembretes.length > 0 && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">Meus lembretes nesta conversa</Typography>
                </Divider>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {lembretes.map((l) => (
                    <Box key={l.id} sx={{ border: `1px solid ${AMBAR}`, borderRadius: 2, p: 1.25, display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "rgba(255, 193, 7, 0.08)" }}>
                      <AlarmIcon sx={{ color: AMBAR, fontSize: 18, mt: 0.25 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.comentario}</Typography>
                        <Typography variant="caption" color="text.secondary">{format(new Date(l.remindAt), "dd/MM/yyyy HH:mm", { locale: ptBR })} · só para você</Typography>
                      </Box>
                      <IconButton size="small" color="error" onClick={() => cancelarLembrete(l.id)} title="Tirar o lembrete">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {scheduled.length > 0 && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t("scheduledMessage.existingTitle")}
                  </Typography>
                </Divider>

                {loadingList ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 280, overflowY: "auto" }}>
                    {scheduled.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 1.5,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              mb: 0.5
                            }}
                          >
                            {item.body}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Typography variant="caption" color="text.secondary">
                              <ScheduleIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: "middle" }} />
                              {format(new Date(item.scheduledAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                            </Typography>
                            <Chip
                              label={statusLabel(item.status)}
                              color={statusColor(item.status)}
                              size="small"
                            />
                          </Box>
                          {item.errorMessage && (
                            <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>
                              <Typography variant="caption">{item.errorMessage}</Typography>
                            </Alert>
                          )}
                        </Box>
                        {item.status === "pending" && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancel(item.id)}
                            title={t("scheduledMessage.cancel")}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            {t("scheduledMessage.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ScheduledMessageModal;
